import React, { useState, useMemo, useEffect } from 'react';
import {
  Button,
  ConfirmationDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DatePicker,
  DropdownField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  VirtualizedMultiSelectCombobox,
} from '@roger/ui-components';
import {
  createFallLog,
  updateFallLog,
  deleteFallLog,
} from '../../services/api';
import type {
  FallLogCreate,
  FallLogUpdate,
  FallLogResponse,
  NatureOfFall,
  YesNoUnknown,
  FallInjuryLevel,
  WitnessRelationship,
  AvailableClinician,
  ClinicPhysicianResponse,
} from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import { useEpisodeDateBounds } from '../../hooks/useEpisodeDateBounds';
import { useLogDelete } from '../../hooks/useLogDelete';
import { parse4xxHeyApiResult } from '../../utils/apiErrorDetail';
import { formatDateToISO } from '../../utils/dateUtils';
import { getClinicianDisplayName } from '../../utils/clinicianUtils';
import { formatPhysicianName } from '../../utils/formatting';
import { FallSourceNotice } from './FallSourceNotice';

interface AddFallLogDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  episodeId?: string | null;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  clinicians: AvailableClinician[];
  physicians: ClinicPhysicianResponse[];
  onCreated: () => void;
  editingFallLog?: FallLogResponse | null;
}

const RadioGroup: React.FC<{
  label: string;
  name: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}> = ({ label, name, options, value, onChange, required }) => (
  <div>
    <p className='text-sm font-medium mb-1.5'>
      {label}
      {required && <span className='text-destructive ml-0.5'>*</span>}
    </p>
    <div className='flex gap-4 flex-wrap'>
      {options.map(opt => (
        <label
          key={opt.value}
          className='flex items-center gap-1.5 cursor-pointer text-sm'
        >
          <input
            type='radio'
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className='accent-primary'
          />
          {opt.label}
        </label>
      ))}
    </div>
  </div>
);

const FieldLabel: React.FC<{
  children: React.ReactNode;
  required?: boolean;
}> = ({ children, required }) => (
  <p className='text-sm font-medium mb-1.5'>
    {children}
    {required && <span className='text-destructive ml-0.5'>*</span>}
  </p>
);

const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const YES_NO_UNKNOWN_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unknown', label: 'Unknown' },
];

const WITNESS_RELATIONSHIP_OPTIONS: {
  value: WitnessRelationship;
  label: string;
}[] = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'parent', label: 'Parent' },
  { value: 'child', label: 'Child' },
  { value: 'other_family_member', label: 'Other Family Member' },
  { value: 'friend', label: 'Friend' },
  { value: 'caregiver', label: 'Caregiver' },
  { value: 'other', label: 'Other' },
  { value: 'unknown', label: 'Unknown' },
];

const INJURY_OPTIONS = [
  {
    value: 'no_injury' as FallInjuryLevel,
    label: 'No Injury',
    description:
      "No evidence noted on assessment; no complaints of pain or injury by the patient; no change in the patient's behavior is noted after the fall.",
  },
  {
    value: 'injury_except_major' as FallInjuryLevel,
    label: 'Injury (Except Major)',
    description:
      'Includes skin tears, abrasions, lacerations, superficial bruises, hematomas, and sprain, or any fall-related injury that causes the patient to complain of pain.',
  },
  {
    value: 'major_injury' as FallInjuryLevel,
    label: 'Major Injury',
    description:
      'Includes bone fractures, joint dislocations, closed head injuries with altered consciousness, subdural hematoma.',
  },
];

export const AddFallLogDialog: React.FC<AddFallLogDialogProps> = ({
  open,
  onClose,
  patientId,
  episodeId,
  episodeStartDate,
  episodeEndDate,
  clinicians,
  physicians,
  onCreated,
  editingFallLog,
}) => {
  const { success: showSuccess, error: showError } = useToast();
  const isEditing = Boolean(editingFallLog);
  const { deleting, confirmDeleteOpen, setConfirmDeleteOpen, handleDelete } =
    useLogDelete({
      entityLabel: 'fall log',
      performDelete: () =>
        deleteFallLog({
          path: { patient_id: patientId, fall_log_id: editingFallLog!.id },
        }),
      onDeleted: () => {
        resetForm();
        onCreated();
      },
    });
  // Fall date must be within the episode and not in the future
  const { minDate: minFallDate, maxDate: maxFallDate } = useEpisodeDateBounds(
    open,
    episodeStartDate,
    episodeEndDate
  );

  const [fallDate, setFallDate] = useState<Date | undefined>(undefined);
  const [natureOfFall, setNatureOfFall] = useState<NatureOfFall | ''>('');
  const [selectedClinicianIds, setSelectedClinicianIds] = useState<string[]>(
    []
  );

  const [witnessPresent, setWitnessPresent] = useState<YesNoUnknown | ''>('');
  const [witnessName, setWitnessName] = useState('');
  const [witnessRelationship, setWitnessRelationship] = useState<
    WitnessRelationship | ''
  >('');

  const [physicianNotifiedByStaff, setPhysicianNotifiedByStaff] = useState<
    'yes' | 'no' | ''
  >('');
  const [physicianNotifiedByStaffDate, setPhysicianNotifiedByStaffDate] =
    useState<Date | undefined>(undefined);
  const [
    physicianNotifiedByStaffPhysicianId,
    setPhysicianNotifiedByStaffPhysicianId,
  ] = useState('');

  const [physicianNotifiedByNonAgency, setPhysicianNotifiedByNonAgency] =
    useState<YesNoUnknown | ''>('');
  const [
    physicianNotifiedByNonAgencyDetail,
    setPhysicianNotifiedByNonAgencyDetail,
  ] = useState('');

  const [physicianProvidedInstructions, setPhysicianProvidedInstructions] =
    useState<'yes' | 'no' | ''>('');
  const [physicianInstructionsDetail, setPhysicianInstructionsDetail] =
    useState('');

  const [injuryLevel, setInjuryLevel] = useState<FallInjuryLevel | ''>('');
  const [injuryDetails, setInjuryDetails] = useState('');

  const [saving, setSaving] = useState(false);

  const clinicianOptions = useMemo(
    () =>
      clinicians.map(c => ({
        value: c.id,
        label: getClinicianDisplayName(c),
      })),
    [clinicians]
  );

  const physicianOptions = useMemo(
    () =>
      physicians.map(p => ({
        value: p.id,
        label: formatPhysicianName(p),
      })),
    [physicians]
  );

  const eitherPhysicianNotified =
    physicianNotifiedByStaff === 'yes' ||
    physicianNotifiedByNonAgency === 'yes';

  const imported = Boolean(editingFallLog?.imported_at);
  const isValid = useMemo(() => {
    if (imported && editingFallLog) {
      if (!fallDate) return false;
      if (
        witnessPresent === 'yes' &&
        ((editingFallLog.witness_name && !witnessName.trim()) ||
          (editingFallLog.witness_relationship && !witnessRelationship))
      )
        return false;
      if (
        physicianNotifiedByStaff === 'yes' &&
        ((editingFallLog.physician_notified_by_staff_date &&
          !physicianNotifiedByStaffDate) ||
          (editingFallLog.physician_notified_by_staff_physician_id &&
            !physicianNotifiedByStaffPhysicianId))
      )
        return false;
      if (
        physicianNotifiedByNonAgency === 'yes' &&
        editingFallLog.physician_notified_by_non_agency_detail &&
        !physicianNotifiedByNonAgencyDetail.trim()
      )
        return false;
      if (
        physicianProvidedInstructions === 'yes' &&
        editingFallLog.physician_instructions_detail &&
        !physicianInstructionsDetail.trim()
      )
        return false;
      if (
        witnessPresent === 'yes' &&
        editingFallLog.witness_present !== 'yes' &&
        (!witnessName.trim() || !witnessRelationship)
      )
        return false;
      if (
        physicianNotifiedByStaff === 'yes' &&
        editingFallLog.physician_notified_by_staff !== true &&
        (!physicianNotifiedByStaffDate || !physicianNotifiedByStaffPhysicianId)
      )
        return false;
      if (
        physicianNotifiedByNonAgency === 'yes' &&
        editingFallLog.physician_notified_by_non_agency !== 'yes' &&
        !physicianNotifiedByNonAgencyDetail.trim()
      )
        return false;
      if (
        physicianProvidedInstructions === 'yes' &&
        editingFallLog.physician_provided_instructions !== true &&
        !physicianInstructionsDetail.trim()
      )
        return false;
      return true;
    }
    if (
      !fallDate ||
      !natureOfFall ||
      !witnessPresent ||
      !physicianNotifiedByStaff ||
      !physicianNotifiedByNonAgency ||
      !injuryLevel
    ) {
      return false;
    }
    if (
      witnessPresent === 'yes' &&
      (!witnessName.trim() || !witnessRelationship)
    ) {
      return false;
    }
    if (
      physicianNotifiedByStaff === 'yes' &&
      (!physicianNotifiedByStaffDate || !physicianNotifiedByStaffPhysicianId)
    ) {
      return false;
    }
    if (
      physicianNotifiedByNonAgency === 'yes' &&
      !physicianNotifiedByNonAgencyDetail.trim()
    ) {
      return false;
    }
    if (eitherPhysicianNotified && !physicianProvidedInstructions) {
      return false;
    }
    if (
      physicianProvidedInstructions === 'yes' &&
      !physicianInstructionsDetail.trim()
    ) {
      return false;
    }
    return true;
  }, [
    imported,
    editingFallLog,
    fallDate,
    natureOfFall,
    witnessPresent,
    witnessName,
    witnessRelationship,
    physicianNotifiedByStaff,
    physicianNotifiedByStaffDate,
    physicianNotifiedByStaffPhysicianId,
    physicianNotifiedByNonAgency,
    physicianNotifiedByNonAgencyDetail,
    eitherPhysicianNotified,
    physicianProvidedInstructions,
    physicianInstructionsDetail,
    injuryLevel,
  ]);

  const resetForm = () => {
    setFallDate(undefined);
    setNatureOfFall('');
    setSelectedClinicianIds([]);
    setWitnessPresent('');
    setWitnessName('');
    setWitnessRelationship('');
    setPhysicianNotifiedByStaff('');
    setPhysicianNotifiedByStaffDate(undefined);
    setPhysicianNotifiedByStaffPhysicianId('');
    setPhysicianNotifiedByNonAgency('');
    setPhysicianNotifiedByNonAgencyDetail('');
    setPhysicianProvidedInstructions('');
    setPhysicianInstructionsDetail('');
    setInjuryLevel('');
    setInjuryDetails('');
  };

  // Pre-fill form when editing an existing fall log
  useEffect(() => {
    if (!open || !editingFallLog) return;
    const d = editingFallLog;
    setFallDate(d.fall_date ? new Date(d.fall_date + 'T00:00:00') : undefined);
    setNatureOfFall((d.nature_of_fall as NatureOfFall) ?? '');
    setWitnessPresent((d.witness_present as YesNoUnknown) ?? '');
    setWitnessName(d.witness_name ?? '');
    setWitnessRelationship(
      (d.witness_relationship as WitnessRelationship) ?? ''
    );
    setPhysicianNotifiedByStaff(
      d.physician_notified_by_staff === true
        ? 'yes'
        : d.physician_notified_by_staff === false
          ? 'no'
          : ''
    );
    setPhysicianNotifiedByStaffDate(
      d.physician_notified_by_staff_date
        ? new Date(d.physician_notified_by_staff_date + 'T00:00:00')
        : undefined
    );
    setPhysicianNotifiedByStaffPhysicianId(
      d.physician_notified_by_staff_physician_id ?? ''
    );
    setPhysicianNotifiedByNonAgency(
      (d.physician_notified_by_non_agency as YesNoUnknown) ?? ''
    );
    setPhysicianNotifiedByNonAgencyDetail(
      d.physician_notified_by_non_agency_detail ?? ''
    );
    setPhysicianProvidedInstructions(
      d.physician_provided_instructions === true
        ? 'yes'
        : d.physician_provided_instructions === false
          ? 'no'
          : ''
    );
    setPhysicianInstructionsDetail(d.physician_instructions_detail ?? '');
    setInjuryLevel((d.injury_level as FallInjuryLevel) ?? '');
    setInjuryDetails(d.injury_details ?? '');
  }, [open, editingFallLog]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (
      !isValid ||
      !fallDate ||
      (!imported &&
        (!natureOfFall ||
          !witnessPresent ||
          !physicianNotifiedByStaff ||
          !physicianNotifiedByNonAgency ||
          !injuryLevel))
    )
      return;

    const attendedStaffNames =
      natureOfFall === 'attended' && selectedClinicianIds.length > 0
        ? selectedClinicianIds
            .map(id => clinicians.find(c => c.id === id))
            .filter((c): c is AvailableClinician => Boolean(c))
            .map(c => getClinicianDisplayName(c))
            .join(', ')
        : natureOfFall === 'attended' &&
            isEditing &&
            editingFallLog?.attended_staff_names
          ? editingFallLog.attended_staff_names // preserve existing names when no re-selection made
          : null;

    setSaving(true);
    try {
      const body: FallLogUpdate = {
        fall_date: formatDateToISO(fallDate),
        nature_of_fall: natureOfFall || null,
        attended_staff_names: attendedStaffNames,
        witness_present: witnessPresent || null,
        witness_name: witnessPresent === 'yes' ? witnessName.trim() : null,
        witness_relationship:
          witnessPresent === 'yes' && witnessRelationship
            ? (witnessRelationship as WitnessRelationship)
            : null,
        physician_notified_by_staff:
          physicianNotifiedByStaff === ''
            ? null
            : physicianNotifiedByStaff === 'yes',
        physician_notified_by_staff_date:
          physicianNotifiedByStaff === 'yes' && physicianNotifiedByStaffDate
            ? formatDateToISO(physicianNotifiedByStaffDate)
            : null,
        physician_notified_by_staff_physician_id:
          physicianNotifiedByStaff === 'yes' &&
          physicianNotifiedByStaffPhysicianId
            ? physicianNotifiedByStaffPhysicianId
            : null,
        physician_notified_by_non_agency: physicianNotifiedByNonAgency || null,
        physician_notified_by_non_agency_detail:
          physicianNotifiedByNonAgency === 'yes'
            ? physicianNotifiedByNonAgencyDetail.trim()
            : null,
        physician_provided_instructions: eitherPhysicianNotified
          ? physicianProvidedInstructions === 'yes'
          : null,
        physician_instructions_detail:
          eitherPhysicianNotified && physicianProvidedInstructions === 'yes'
            ? physicianInstructionsDetail.trim()
            : null,
        injury_level: injuryLevel || null,
        injury_details: injuryDetails.trim() || null,
        // Stamp the selected episode only on create — edits keep the log's
        // own episode (the backend leaves fields absent from the body untouched).
        ...(isEditing ? {} : { episode_id: episodeId ?? null }),
      };

      if (imported && editingFallLog) {
        const original = editingFallLog;
        const same = (left: unknown, right: unknown) =>
          (left ?? '') === (right ?? '');
        const remove = (keys: (keyof FallLogUpdate)[]) =>
          keys.forEach(key => {
            delete body[key];
          });
        if (
          same(witnessPresent, original.witness_present) &&
          same(witnessName, original.witness_name) &&
          same(witnessRelationship, original.witness_relationship)
        )
          remove(['witness_present', 'witness_name', 'witness_relationship']);
        if (
          same(
            physicianNotifiedByStaff,
            original.physician_notified_by_staff === true
              ? 'yes'
              : original.physician_notified_by_staff === false
                ? 'no'
                : ''
          ) &&
          same(
            physicianNotifiedByStaffDate
              ? formatDateToISO(physicianNotifiedByStaffDate)
              : null,
            original.physician_notified_by_staff_date
          ) &&
          same(
            physicianNotifiedByStaffPhysicianId,
            original.physician_notified_by_staff_physician_id
          )
        )
          remove([
            'physician_notified_by_staff',
            'physician_notified_by_staff_date',
            'physician_notified_by_staff_physician_id',
          ]);
        if (
          same(
            physicianNotifiedByNonAgency,
            original.physician_notified_by_non_agency
          ) &&
          same(
            physicianNotifiedByNonAgencyDetail,
            original.physician_notified_by_non_agency_detail
          )
        )
          remove([
            'physician_notified_by_non_agency',
            'physician_notified_by_non_agency_detail',
          ]);
        if (
          same(
            physicianProvidedInstructions,
            original.physician_provided_instructions === true
              ? 'yes'
              : original.physician_provided_instructions === false
                ? 'no'
                : ''
          ) &&
          same(
            physicianInstructionsDetail,
            original.physician_instructions_detail
          )
        )
          remove([
            'physician_provided_instructions',
            'physician_instructions_detail',
          ]);
        if (
          same(natureOfFall, original.nature_of_fall) &&
          selectedClinicianIds.length === 0
        )
          remove(['nature_of_fall', 'attended_staff_names']);
        if (same(injuryLevel, original.injury_level)) remove(['injury_level']);
        if (same(injuryDetails, original.injury_details))
          remove(['injury_details']);
        if (same(formatDateToISO(fallDate), original.fall_date))
          remove(['fall_date']);
      }

      let res;
      if (isEditing && editingFallLog) {
        res = await updateFallLog({
          path: { patient_id: patientId, fall_log_id: editingFallLog.id },
          body,
        });
      } else {
        res = await createFallLog({
          path: { patient_id: patientId },
          body: {
            ...body,
            fall_date: formatDateToISO(fallDate),
            nature_of_fall: natureOfFall as NatureOfFall,
            witness_present: witnessPresent as YesNoUnknown,
            physician_notified_by_staff: physicianNotifiedByStaff === 'yes',
            physician_notified_by_non_agency:
              physicianNotifiedByNonAgency as YesNoUnknown,
            injury_level: injuryLevel as FallInjuryLevel,
          } satisfies FallLogCreate,
        });
      }
      if (res.error) {
        console.error('Failed to save fall log:', res.error);
        // Surface actionable validation messages (e.g. date outside the
        // episode) — the date pickers can't catch every case, such as
        // timezone differences with the server's business date.
        const { message } = parse4xxHeyApiResult(res);
        showError('Save Failed', message ?? 'Failed to save fall log.');
        return;
      }
      showSuccess('Fall Log Saved', 'Fall log saved.');
      handleClose();
      onCreated();
    } catch (err) {
      console.error('Error creating fall log:', err);
      showError('Create Failed', 'Failed to save fall log.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Fall Log' : 'Add Fall Log'}
          </DialogTitle>
          <DialogDescription className='sr-only'>
            Record a patient fall event.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-5'>
          {editingFallLog?.source_evidence && (
            <FallSourceNotice evidence={editingFallLog.source_evidence} />
          )}
          {/* Date */}
          <div>
            <FieldLabel required>Date of Fall</FieldLabel>
            <DatePicker
              date={fallDate}
              onDateChange={setFallDate}
              minDate={minFallDate}
              maxDate={maxFallDate}
            />
          </div>

          {/* Nature of Fall */}
          <div>
            <FieldLabel required={!imported}>Nature of Fall</FieldLabel>
            <div className='space-y-3'>
              <label className='flex items-start gap-2.5 cursor-pointer'>
                <input
                  type='radio'
                  name='natureOfFall'
                  value='attended'
                  checked={natureOfFall === 'attended'}
                  onChange={() => setNatureOfFall('attended')}
                  className='mt-0.5 accent-primary'
                />
                <span>
                  <span className='text-sm font-medium'>Attended</span>
                  <span className='block text-xs text-muted-foreground mt-0.5'>
                    The patient fell while being assisted or in the presence of
                    an agency staff member.
                  </span>
                </span>
              </label>

              {natureOfFall === 'attended' && (
                <div className='ml-6'>
                  <FieldLabel>Staff Present</FieldLabel>
                  <VirtualizedMultiSelectCombobox
                    options={clinicianOptions}
                    value={selectedClinicianIds}
                    onValueChange={setSelectedClinicianIds}
                    placeholder='Select staff members…'
                    searchPlaceholder='Search clinicians…'
                    height='200px'
                  />
                </div>
              )}

              <label className='flex items-start gap-2.5 cursor-pointer'>
                <input
                  type='radio'
                  name='natureOfFall'
                  value='unattended'
                  checked={natureOfFall === 'unattended'}
                  onChange={() => setNatureOfFall('unattended')}
                  className='mt-0.5 accent-primary'
                />
                <span>
                  <span className='text-sm font-medium'>Unattended</span>
                  <span className='block text-xs text-muted-foreground mt-0.5'>
                    The patient fell when an agency staff member was not present
                    to observe the fall.
                  </span>
                </span>
              </label>
            </div>
          </div>

          {/* Witness */}
          <div className='space-y-3'>
            <RadioGroup
              label='Was there a witness present other than an agency staff member?'
              name='witnessPresent'
              options={YES_NO_UNKNOWN_OPTIONS}
              value={witnessPresent}
              onChange={v => setWitnessPresent(v as YesNoUnknown)}
              required={!imported}
            />

            {witnessPresent === 'yes' && (
              <div className='ml-6 space-y-3'>
                <div>
                  <FieldLabel required={!imported}>Witness Name</FieldLabel>
                  <input
                    type='text'
                    value={witnessName}
                    onChange={e => setWitnessName(e.target.value)}
                    placeholder='Full name'
                    className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  />
                </div>
                <div>
                  <FieldLabel required={!imported}>
                    Witness Relationship
                  </FieldLabel>
                  <Select
                    value={witnessRelationship}
                    onValueChange={v =>
                      setWitnessRelationship(v as WitnessRelationship)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select relationship…' />
                    </SelectTrigger>
                    <SelectContent>
                      {WITNESS_RELATIONSHIP_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          {/* Physician Communication */}
          <div className='space-y-3'>
            <p className='text-sm font-semibold'>Physician Communication</p>

            <RadioGroup
              label='Was a physician notified by an agency staff member?'
              name='physicianNotifiedByStaff'
              options={YES_NO_OPTIONS}
              value={physicianNotifiedByStaff}
              onChange={v => setPhysicianNotifiedByStaff(v as 'yes' | 'no')}
              required={!imported}
            />

            {physicianNotifiedByStaff === 'yes' && (
              <div className='ml-6 space-y-3'>
                <div>
                  <FieldLabel required={!imported}>Date Notified</FieldLabel>
                  <DatePicker
                    date={physicianNotifiedByStaffDate}
                    onDateChange={setPhysicianNotifiedByStaffDate}
                  />
                </div>
                <div>
                  <DropdownField
                    label='Physician Notified'
                    required={!imported}
                    htmlFor='fall-log-notified-physician'
                    sortOptions
                    placeholder='Select physician'
                    searchPlaceholder='Search physicians...'
                    enableFiltering
                    isClearable={false}
                    popoverWidth='min(28rem, 90vw)'
                    options={physicianOptions}
                    value={physicianNotifiedByStaffPhysicianId}
                    onChange={setPhysicianNotifiedByStaffPhysicianId}
                  />
                </div>
              </div>
            )}

            <RadioGroup
              label='Was a physician notified by a non-agency individual?'
              name='physicianNotifiedByNonAgency'
              options={YES_NO_UNKNOWN_OPTIONS}
              value={physicianNotifiedByNonAgency}
              onChange={v => setPhysicianNotifiedByNonAgency(v as YesNoUnknown)}
              required={!imported}
            />

            {physicianNotifiedByNonAgency === 'yes' && (
              <div className='ml-6'>
                <FieldLabel required={!imported}>Please specify</FieldLabel>
                <textarea
                  value={physicianNotifiedByNonAgencyDetail}
                  onChange={e =>
                    setPhysicianNotifiedByNonAgencyDetail(e.target.value)
                  }
                  placeholder='Who notified the physician and how?'
                  rows={2}
                  className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none'
                />
              </div>
            )}

            {eitherPhysicianNotified && (
              <div className='space-y-3'>
                <RadioGroup
                  label='Did the physician provide any instructions related to the fall for the patient?'
                  name='physicianProvidedInstructions'
                  options={YES_NO_OPTIONS}
                  value={physicianProvidedInstructions}
                  onChange={v =>
                    setPhysicianProvidedInstructions(v as 'yes' | 'no')
                  }
                  required={!imported}
                />
                {physicianProvidedInstructions === 'yes' && (
                  <div className='ml-6'>
                    <FieldLabel required={!imported}>
                      Physician Instructions
                    </FieldLabel>
                    <textarea
                      value={physicianInstructionsDetail}
                      onChange={e =>
                        setPhysicianInstructionsDetail(e.target.value)
                      }
                      placeholder="Describe the physician's instructions…"
                      rows={3}
                      className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none'
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Injury Level */}
          <div>
            <FieldLabel required={!imported}>
              Did an injury occur as a result of the fall?
            </FieldLabel>
            <p className='text-xs text-muted-foreground mb-3'>
              If the patient has multiple injuries, code the fall for the
              highest level of injury.
            </p>
            {imported && !injuryLevel && (
              <div className='mb-3'>
                <FieldLabel>Injury details</FieldLabel>
                <textarea
                  aria-label='Injury details'
                  value={injuryDetails}
                  onChange={e => setInjuryDetails(e.target.value)}
                  rows={3}
                  className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                />
              </div>
            )}
            <div className='space-y-3'>
              {INJURY_OPTIONS.map(opt => (
                <div key={opt.value}>
                  <label className='flex items-start gap-2.5 cursor-pointer'>
                    <input
                      type='radio'
                      name='injuryLevel'
                      value={opt.value}
                      checked={injuryLevel === opt.value}
                      onChange={() => setInjuryLevel(opt.value)}
                      className='mt-0.5 accent-primary'
                    />
                    <span>
                      <span className='text-sm font-medium'>{opt.label}</span>
                      <span className='block text-xs text-muted-foreground mt-0.5'>
                        {opt.description}
                      </span>
                    </span>
                  </label>
                  {injuryLevel === opt.value && (
                    <div className='ml-6 mt-2'>
                      <FieldLabel>Details</FieldLabel>
                      <textarea
                        value={injuryDetails}
                        onChange={e => setInjuryDetails(e.target.value)}
                        placeholder='Add details…'
                        rows={3}
                        className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none'
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className='gap-2 mt-2'>
          <Button
            variant='outline'
            onClick={handleClose}
            disabled={saving || deleting}
          >
            Cancel
          </Button>
          {isEditing && (
            <Button
              variant='destructive'
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={saving || deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={!isValid || saving || deleting}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
        <ConfirmationDialog
          open={confirmDeleteOpen}
          title='Delete Fall Log'
          message='This will permanently delete this fall log. This action cannot be undone.'
          confirmLabel='Delete'
          loading={deleting}
          onAccept={handleDelete}
          onCancel={() => setConfirmDeleteOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
};
